import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  FolderTree,
  Settings,
  MessageSquare,
  Newspaper,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Award,
  Briefcase,
  Palette,
  Globe,
  FileImage,
  ClipboardList,
  FileCheck,
  Layers,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { adminAuth } from '@/services/adminAuth';
import { toast } from 'sonner';

const navItems = [
  { path: '/admin', label: '控制台', icon: <LayoutDashboard className="h-5 w-5" /> },
  { path: '/admin/products', label: '产品管理', icon: <Package className="h-5 w-5" /> },
  { path: '/admin/categories', label: '分类管理', icon: <FolderTree className="h-5 w-5" /> },
  { path: '/admin/params', label: '参数管理', icon: <Settings className="h-5 w-5" /> },
  { path: '/admin/inquiries', label: '询单管理', icon: <MessageSquare className="h-5 w-5" /> },
  { path: '/admin/news', label: '新闻管理', icon: <Newspaper className="h-5 w-5" /> },
  { path: '/admin/applications', label: '应用领域', icon: <Layers className="h-5 w-5" /> },
  { path: '/admin/honors', label: '荣誉资质', icon: <Award className="h-5 w-5" /> },
  { path: '/admin/jobs', label: '招聘职位', icon: <Briefcase className="h-5 w-5" /> },
  { path: '/admin/job-applications', label: '简历投递', icon: <FileCheck className="h-5 w-5" /> },
  { path: '/admin/page-content', label: '页面内容', icon: <Palette className="h-5 w-5" /> },
  { path: '/admin/attachments', label: '附件管理', icon: <FileImage className="h-5 w-5" /> },
  { path: '/admin/system-config', label: '系统配置', icon: <Globe className="h-5 w-5" /> },
  { path: '/admin/operation-logs', label: '操作日志', icon: <ClipboardList className="h-5 w-5" /> },
];

// 仅超级管理员可见的菜单
const superAdminItems = [
  { path: '/admin/admins', label: '管理员设置', icon: <Users className="h-5 w-5" /> },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminName, setAdminName] = useState('管理员');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // 检查认证状态
    if (!adminAuth.isLoggedIn()) {
      navigate('/admin/login');
    } else {
      setIsAuthenticated(true);
      const info = adminAuth.getAdminInfo();
      if (info) {
        setAdminName(info.name || info.username);
        setIsSuperAdmin(info.role === 'super');
      }
    }
  }, [navigate]);

  const handleLogout = () => {
    adminAuth.logout();
    toast.success('已退出登录');
    navigate('/admin/login');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-gray-900 text-white transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:static lg:translate-x-0 w-64 flex flex-col`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
          <Link to="/admin" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
              <span className="font-bold">H</span>
            </div>
            <span className="font-bold">管理后台</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                location.pathname === item.path
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
          {isSuperAdmin && superAdminItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                location.pathname === item.path
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="shrink-0 p-4 border-t border-gray-800">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 text-gray-400 hover:text-white w-full"
          >
            <LogOut className="h-5 w-5" />
            <span>退出登录</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white shadow-sm h-16 flex items-center px-4 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden mr-4"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex-1" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  <span className="font-bold">{adminName.charAt(0).toUpperCase()}</span>
                </div>
                <span>{adminName}</span>
                {isSuperAdmin && <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">超管</span>}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                退出登录
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
